'use client';

import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HUB_EVENTS, pushDataLayer } from '@/lib/analytics';
import Countdown from './countdown';
import { ArrowDownIcon, ArrowUpRight, ClockIcon, NavigationIcon } from './icons';
import Reveal from './reveal';
import type { MapPin } from './drops-map';
import type { HubContent } from '@/lib/content/hub';
import type { LockedDrop, RevealedDrop } from '@/lib/hub/types';

const DropsMap = dynamic(() => import('./drops-map'), {
  ssr: false,
  loading: () => <div className="h-[420px] w-full animate-pulse bg-cream/[0.04] sm:h-[560px]" />,
});

function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''));
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function PinIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

type MapSectionProps = {
  copy: HubContent['map'];
  countdown: HubContent['countdown'];
  revealedDrops: RevealedDrop[];
  lockedDrops: LockedDrop[];
  serverNow: number;
};

export default function MapSection({
  copy,
  countdown,
  revealedDrops,
  lockedDrops,
  serverNow,
}: MapSectionProps) {
  const router = useRouter();
  const total = revealedDrops.length + lockedDrops.length;

  const rows = useMemo(
    () =>
      [
        ...revealedDrops.map((drop) => ({
          order: drop.order,
          id: drop.id,
          revealed: true as const,
        })),
        ...lockedDrops.map((drop) => ({
          order: drop.order,
          id: drop.id,
          revealed: false as const,
        })),
      ].sort((a, b) => a.order - b.order),
    [revealedDrops, lockedDrops],
  );

  const [selectedId, setSelectedId] = useState<string>(() => rows[0]?.id ?? '');
  const selectedRevealed = revealedDrops.find((drop) => drop.id === selectedId) ?? null;
  const selectedLocked = lockedDrops.find((drop) => drop.id === selectedId) ?? null;
  const selected: {
    order: number;
    name: string;
    barrio: string;
    hero: boolean;
    photo: string;
  } | null = selectedRevealed ?? selectedLocked;

  const select = useCallback((id: string) => {
    setSelectedId(id);
    pushDataLayer(HUB_EVENTS.NAV_CLICK, { section: 'map', id });
  }, []);

  // One workshop open at a time, keyed by `${dropId}:${index}` — selecting a
  // different drop naturally has no match, so nothing stays open across it.
  const [openWorkshop, setOpenWorkshop] = useState<string | null>(null);
  const toggleWorkshop = useCallback((key: string) => {
    setOpenWorkshop((current) => (current === key ? null : key));
  }, []);

  // Next locked drop and whether the remaining locked drops all share one time.
  const nextLocked = [...lockedDrops].sort(
    (a, b) => Date.parse(a.liveAt) - Date.parse(b.liveAt),
  )[0];
  const grouped =
    lockedDrops.length > 1 && new Set(lockedDrops.map((drop) => drop.liveAt)).size === 1;
  const someRevealed = revealedDrops.length > 0;

  // Every drop sits on the map — locked ones in their dashed style (matches the
  // comp). Only the live/locked flag, order and coords are needed here.
  const pins: MapPin[] = useMemo(
    () =>
      [
        ...revealedDrops.map((drop) => ({ drop, live: true })),
        ...lockedDrops.map((drop) => ({ drop, live: false })),
      ]
        .sort((a, b) => a.drop.order - b.drop.order)
        .map(({ drop, live }) => ({
          id: drop.id,
          order: drop.order,
          lat: drop.lat,
          lng: drop.lng,
          name: drop.name,
          barrio: drop.barrio,
          live,
          locationTbc: drop.locationTbc,
        })),
    [revealedDrops, lockedDrops],
  );

  // ISR is up to 30s stale — once the earliest locked time passes locally, nudge
  // the server until its render includes the newly-live drop.
  const [reachedZero, setReachedZero] = useState(false);
  const handleZero = useCallback(() => {
    if (nextLocked) pushDataLayer(HUB_EVENTS.DROP_REVEALED, { id: nextLocked.id });
    setReachedZero(true);
  }, [nextLocked]);

  useEffect(() => {
    if (!reachedZero) return;
    const first = window.setTimeout(() => router.refresh(), 2_000);
    const poll = window.setInterval(() => router.refresh(), 10_000);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(poll);
    };
  }, [reachedZero, router]);

  const postLiveParts = copy.postLive.split('{instagram}');

  const nextLabel =
    nextLocked ?
      grouped ?
        someRevealed ? copy.dayTwoOpens
        : copy.allOpenIn
      : fill(copy.nextReveal, { n: nextLocked.order })
    : copy.everyLive;

  return (
    <section id="map" className="hub-section bg-coal py-20 sm:py-28">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal direction="left">
          <header className="max-w-2xl">
            <p className="hub-label-lg text-yamal">{copy.eyebrow}</p>
            <h2 className="hub-display mt-4 text-[clamp(2.2rem,6vw,4.2rem)] text-cream">
              {copy.headline}
            </h2>
          </header>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
          {/* Map */}
          <Reveal
            direction="left"
            delayMs={100}
            className="relative overflow-hidden border border-cream/15"
          >
            <DropsMap
              pins={pins}
              selectedId={selectedId}
              onSelect={select}
              ariaLabel={copy.mapAria}
            />
            <div className="pointer-events-none absolute top-4 left-4 z-[500] flex items-center gap-2 bg-coal/85 px-3 py-2 backdrop-blur-sm">
              <span className="size-1.5 animate-pulse rounded-full bg-yamal" aria-hidden="true" />
              <span className="hub-label text-cream/85">
                {revealedDrops.length === total ?
                  copy.everyLive
                : fill(copy.live, { n: revealedDrops.length, m: total })}
              </span>
            </div>
          </Reveal>

          {/* Rail */}
          <Reveal direction="right" delayMs={200} className="flex flex-col gap-6">
            <div className="border border-yamal/35 bg-yamal/[0.08] p-6">
              <p className="hub-label text-yamal">{nextLabel}</p>
              {nextLocked ?
                <>
                  <p className="hub-display mt-3 text-3xl text-cream">
                    {grouped ? fill(copy.locations, { n: lockedDrops.length }) : nextLocked.barrio}
                  </p>
                  <div className="mt-4">
                    <Countdown
                      targetIso={nextLocked.liveAt}
                      serverNow={serverNow}
                      units={countdown.units}
                      doneLabel={copy.liveTag}
                      liveLabel={countdown.live}
                      size="sm"
                      onZero={handleZero}
                    />
                  </div>
                  <p className="hub-label-sm mt-4 text-cream/60">{nextLocked.revealAtLabel}</p>
                </>
              : <p className="mt-3 text-[17px] leading-relaxed whitespace-pre-line text-cream/75">
                  {postLiveParts[0]}
                  <a
                    href={copy.instagramUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => pushDataLayer(HUB_EVENTS.NAV_CLICK, { section: 'instagram' })}
                    className="text-yamal underline underline-offset-2"
                  >
                    {copy.instagramHandle}
                  </a>
                  {postLiveParts[1]}
                </p>
              }
            </div>

            <ul className="divide-y divide-cream/10 border border-cream/15">
              {rows.map((row) => {
                const active = row.id === selectedId;
                const revealed = revealedDrops.find((drop) => drop.id === row.id);
                const locked = lockedDrops.find((drop) => drop.id === row.id);
                const drop = revealed ?? locked;
                return (
                  <li key={row.id} className={active ? 'bg-cream/[0.08]' : ''}>
                    <button
                      type="button"
                      onClick={() => select(row.id)}
                      aria-current={active}
                      className={`flex min-h-[64px] w-full items-center gap-4 px-4 py-3.5 text-left transition-colors active:bg-cream/[0.12] ${
                        active ? '' : 'hover:bg-cream/[0.05]'
                      }`}
                    >
                      <span
                        className={`hub-display grid size-10 shrink-0 place-items-center text-[18px] ${
                          revealed ? 'bg-yamal text-coal' : 'border border-cream/35 text-cream/60'
                        }`}
                      >
                        {row.order}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="hub-display block truncate text-[20px] leading-[1.25] text-cream">
                          {drop ?
                            fill(revealed ? copy.locationLabel : copy.locationLabelLocked, {
                              nn: String(row.order).padStart(2, '0'),
                              name: drop.name,
                              barrio: drop.barrio,
                            })
                          : copy.locked}
                        </span>
                        <span className="mt-0.5 flex min-w-0 items-center gap-2">
                          <span className="hub-label-sm truncate text-cream/60">
                            {drop?.barrio}
                          </span>
                          {drop?.hero ?
                            <span className="hub-label-xs shrink-0 bg-yamal px-1.5 py-0.5 text-coal">
                              {copy.mainEvent}
                            </span>
                          : null}
                        </span>
                      </span>
                      {revealed ?
                        <span className="hub-label-sm shrink-0 text-yamal">{copy.liveTag}</span>
                      : <LockIcon className="size-4 shrink-0 text-cream/45" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </Reveal>
        </div>

        {/* Selected detail */}
        {selected ?
          <Reveal className="mt-6 grid gap-0 border border-cream/15 md:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
            <div className="relative aspect-[4/3] overflow-hidden bg-coal md:aspect-auto md:min-h-[420px] md:max-h-[560px]">
              <Image
                src={selected.photo}
                alt={selectedRevealed ? selected.barrio : ''}
                fill
                sizes="(min-width: 768px) 320px, 100vw"
                className={`object-cover object-center transition-all duration-700 ${
                  selectedRevealed ? ''
                  : selected.order > 1 ? 'scale-150'
                  : 'scale-105'
                } ${selectedRevealed ? '' : 'blur-[2px] brightness-[0.28] grayscale'}`}
              />
              {selectedRevealed ? null : (
                <div className="absolute inset-0 grid place-items-center">
                  <LockIcon className="size-7 text-cream/55" />
                </div>
              )}
            </div>

            <div className="p-6 sm:p-8">
              <p className="flex flex-wrap items-center gap-3">
                <span className="hub-label text-cream/60">
                  {fill(selectedRevealed ? copy.locationLabel : copy.locationLabelLocked, {
                    nn: String(selected.order).padStart(2, '0'),
                    name: selected.name,
                    barrio: selected.barrio,
                  })}
                </span>
                {selected.hero ?
                  <span className="hub-label-xs bg-yamal px-2 py-1 text-coal">
                    {copy.mainEvent}
                  </span>
                : null}
              </p>
              <h3 className="hub-display mt-3 text-3xl text-cream">{selected.barrio}</h3>
              <p className="mt-4 max-w-prose text-[17px] leading-relaxed whitespace-pre-line text-cream/75">
                {selectedRevealed ? selectedRevealed.blurb : selectedLocked?.teaser}
              </p>

              {selectedRevealed ?
                <>
                  <dl className="mt-6 grid gap-5 border-t border-cream/[0.12] pt-6 sm:grid-cols-2">
                    <div>
                      <dt className="hub-label-sm flex items-center gap-2 text-yamal">
                        <ClockIcon className="size-4" />
                        {copy.hoursLabel}
                      </dt>
                      <dd className="mt-2 text-[17px] leading-relaxed whitespace-pre-line text-cream/80">
                        {selectedRevealed.hours}
                      </dd>
                    </div>
                    {selectedRevealed.transit ?
                      <div>
                        <dt className="hub-label-sm flex items-center gap-2 text-yamal">
                          <PinIcon className="size-4" />
                          {copy.gettingThere}
                        </dt>
                        <dd className="mt-2 text-[17px] leading-relaxed text-cream/80">
                          {selectedRevealed.transit}
                        </dd>
                      </div>
                    : null}
                    <div className="sm:col-span-2">
                      <dt className="hub-label-sm text-yamal">{copy.whatsOnLabel}</dt>
                      <dd className="mt-2">
                        <ul className="space-y-1.5">
                          {selectedRevealed.whatsOn.map((item) => (
                            <li
                              key={item}
                              className="flex gap-2.5 text-[17px] leading-relaxed text-cream/80"
                            >
                              <span
                                className="mt-[0.6em] size-1.5 shrink-0 bg-yamal"
                                aria-hidden="true"
                              />
                              {item}
                            </li>
                          ))}
                          {selectedRevealed.workshops.map((workshop, index) => {
                            const key = `${selectedRevealed.id}:${index}`;
                            const expanded = openWorkshop === key;
                            return (
                              <li
                                key={key}
                                className="border-t border-cream/[0.12] pt-2.5 first:border-t-0 first:pt-0"
                              >
                                <button
                                  type="button"
                                  aria-expanded={expanded}
                                  onClick={() => toggleWorkshop(key)}
                                  className="flex w-full items-center justify-between gap-3 py-1 text-left text-[17px] leading-relaxed text-cream/80"
                                >
                                  <span className="font-semibold">{workshop.title}</span>
                                  <ArrowDownIcon
                                    className={`size-4 shrink-0 text-cream/50 transition-transform ${
                                      expanded ? 'rotate-180' : ''
                                    }`}
                                  />
                                </button>
                                {expanded ?
                                  <div className="mt-2 pb-2 text-[15px] leading-relaxed text-cream/70">
                                    {workshop.body.map((paragraph, i) => {
                                      // Bold the "About X:" / "Sobre X:" lead-in.
                                      const about = /^((?:About|Sobre) [^:]+:)(.*)$/s.exec(
                                        paragraph,
                                      );
                                      return (
                                        <p key={paragraph} className={i > 0 ? 'mt-4' : ''}>
                                          {about ?
                                            <>
                                              <strong className="font-semibold text-cream/85">
                                                {about[1]}
                                              </strong>
                                              {about[2]}
                                            </>
                                          : paragraph}
                                        </p>
                                      );
                                    })}
                                  </div>
                                : null}
                              </li>
                            );
                          })}
                        </ul>
                      </dd>
                    </div>
                  </dl>

                  {selectedRevealed.workshopsUrl ?
                    <a
                      href={selectedRevealed.workshopsUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() =>
                        pushDataLayer(HUB_EVENTS.NAV_CLICK, {
                          section: 'workshops',
                          id: selectedRevealed.id,
                        })
                      }
                      className="hub-display mt-6 inline-flex min-h-[52px] items-center gap-2 border border-cream/35 px-6 py-4 text-[20px] tracking-[0.02em] text-cream transition-colors hover:border-yamal hover:text-yamal"
                    >
                      <ArrowUpRight className="size-5" />
                      {copy.registerWorkshops}
                    </a>
                  : null}

                  <div className="mt-6 flex flex-wrap items-center gap-4">
                    {selectedRevealed.locationTbc ?
                      <p className="hub-label text-yamal">{selectedRevealed.address}</p>
                    : <>
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${selectedRevealed.lat},${selectedRevealed.lng}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() =>
                            pushDataLayer(HUB_EVENTS.NAV_CLICK, {
                              section: 'directions',
                              id: selectedRevealed.id,
                            })
                          }
                          className="hub-display inline-flex min-h-[52px] items-center gap-2 bg-yamal px-6 py-4 text-[20px] tracking-[0.02em] text-coal transition-transform hover:-translate-y-0.5 active:translate-y-0"
                        >
                          <NavigationIcon className="size-5" />
                          {copy.getDirections}
                        </a>
                        <p className="hub-label-sm text-cream/65">{selectedRevealed.address}</p>
                      </>
                    }
                  </div>
                </>
              : selectedLocked ?
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <Countdown
                    targetIso={selectedLocked.liveAt}
                    serverNow={serverNow}
                    units={countdown.units}
                    doneLabel={copy.liveTag}
                    liveLabel={countdown.live}
                    size="sm"
                    onZero={handleZero}
                  />
                  <p className="hub-label-sm text-cream/60">
                    {copy.unlocks} {selectedLocked.revealAtLabel}
                  </p>
                </div>
              : null}
            </div>
          </Reveal>
        : null}
      </div>
    </section>
  );
}
